import React from 'react';
import { SprayCan, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PartLookupInput from '../ui/part-lookup-input';
import ZeroValueBadge from './ZeroValueBadge';

const PaintButton = ({
  part,
  zoneId,
  onUpdatePart,
  childBlendParts = [],
  onAddBlendPart,
  onRemoveBlendPart,
  onUpdateBlendPart,
  insurerConfigId,
  hasInOutPaint,
  paintSizeOptions = [],
  blendUnitPrice = null,
  defaultsReady = false,
}) => {
  const isActive = part.paint;
  // Effective paint cost: manual override wins, else the insurer line's default.
  const effectivePaintCost = part.paintCostOverride ?? part.defaultPaintCost;
  const needsAttention = defaultsReady && isActive && !(Number(effectivePaintCost) > 0);
  const paintSize = part.paintSize || 'standard';
  // Only offered when the line actually has a small/large variant. Insurers with no
  // size variants, and those sized off the vehicle rather than the panel, get none.
  const showSizes = paintSizeOptions.length > 1;
  const sizeLabel = paintSizeOptions.find(o => o.value === paintSize)?.label;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`relative h-8 gap-1.5 text-xs ${
            isActive
              ? 'bg-chart-4 border-chart-4 text-white hover:bg-chart-4/90 hover:text-white'
              : 'text-muted-foreground border-border hover:text-foreground'
          }`}
        >
          {needsAttention && <ZeroValueBadge title="No paint cost set — this line will be blank on the quote" />}
          <SprayCan className="h-3.5 w-3.5" />
          Paint
          {isActive && part.paintScope === 'innerOuter' && (
            <span className="ml-0.5 text-[10px] bg-white/25 text-white rounded px-1">
              In & Out
            </span>
          )}
          {isActive && showSizes && paintSize !== 'standard' && sizeLabel && (
            <span className="ml-0.5 text-[10px] bg-white/25 text-white rounded px-1">
              {sizeLabel}
            </span>
          )}
          {isActive && childBlendParts.length > 0 && (
            <span className="ml-0.5 text-[10px] bg-white/25 text-white rounded-full px-1.5">
              +{childBlendParts.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px]" align="start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`paint-pop-${part.id}`}
              checked={isActive}
              onCheckedChange={(checked) => onUpdatePart(zoneId, part.id, 'paint', checked)}
            />
            <Label htmlFor={`paint-pop-${part.id}`} className="text-xs cursor-pointer font-medium">
              Paint
            </Label>
          </div>

          {isActive && (
            <>
              {/* Paint scope toggle */}
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant={part.paintScope !== 'innerOuter' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 text-xs h-7"
                  onClick={() => onUpdatePart(zoneId, part.id, 'paintScope', 'outer')}
                >
                  Outer
                </Button>
                <Button
                  type="button"
                  variant={part.paintScope === 'innerOuter' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 text-xs h-7"
                  onClick={() => onUpdatePart(zoneId, part.id, 'paintScope', 'innerOuter')}
                >
                  In & Out
                </Button>
              </div>

              {/* Panel size — only shown when this line has a small/large variant */}
              {showSizes && (
                <div className="flex gap-1">
                  {paintSizeOptions.map(option => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={paintSize === option.value ? 'default' : 'outline'}
                      size="sm"
                      className="flex-1 text-xs h-7"
                      onClick={() => onUpdatePart(zoneId, part.id, 'paintSize', option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              )}

              {/* Paint cost override */}
              <div className="space-y-1">
                <Label htmlFor={`paintCost-${part.id}`} className="text-xs text-muted-foreground">
                  Paint Cost ($)
                </Label>
                <Input
                  id={`paintCost-${part.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={part.defaultPaintCost != null ? `$${part.defaultPaintCost}` : '0'}
                  value={part.paintCostOverride ?? ''}
                  onChange={(e) => onUpdatePart(zoneId, part.id, 'paintCostOverride', e.target.value ? Number(e.target.value) : null)}
                  className="h-7 text-xs"
                />
              </div>

              {/* Blend parts */}
              <div className="space-y-2 pt-1 border-t">
                <div className="text-xs font-medium text-muted-foreground flex items-center justify-between">
                  <span>{childBlendParts.length} blend part{childBlendParts.length !== 1 ? 's' : ''}</span>
                  {childBlendParts.length > 0 && <span className="text-[10px]">Blend Rate</span>}
                </div>
                {childBlendParts.length > 0 && (
                  <div className="space-y-1.5">
                    {childBlendParts.map(blendPart => (
                      <div key={blendPart.id} className="flex items-center gap-1.5">
                        <PartLookupInput
                          value={blendPart.part}
                          onChange={(partName) => onUpdateBlendPart(zoneId, blendPart.id, 'part', partName)}
                          insurerConfigId={insurerConfigId}
                          zone={zoneId}
                          placeholder="Part name"
                          disabled={!insurerConfigId}
                          className="mb-0 flex-1"
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={blendUnitPrice != null ? `$${blendUnitPrice}` : '0'}
                          value={blendPart.blendRateOverride ?? ''}
                          onChange={(e) => onUpdateBlendPart(zoneId, blendPart.id, 'blendRateOverride', e.target.value ? Number(e.target.value) : null)}
                          className="h-7 w-20 text-xs shrink-0"
                          title="Blend rate ($)"
                        />
                        <button
                          type="button"
                          onClick={() => onRemoveBlendPart(zoneId, blendPart.id)}
                          className="p-1 text-muted-foreground hover:text-destructive flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <PartLookupInput
                  value=""
                  onChange={(partName) => {
                    if (partName && partName.trim()) {
                      onAddBlendPart(zoneId, part, partName);
                    }
                  }}
                  insurerConfigId={insurerConfigId}
                  zone={zoneId}
                  placeholder="Add blend part..."
                  disabled={!insurerConfigId}
                  commitOnBlur={false}
                  clearOnChange={true}
                  className="mb-0"
                />
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PaintButton;
